import React, { useContext, useEffect } from "react";
import "./App.css";
import {
  BrowserRouter,
  Switch,
  Route,
  Redirect,
  RouteProps,
  useRouteMatch,
} from "react-router-dom";
import { Header } from "./components/header/Header";
import { Settings } from "./pages/settings/Settings";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { Scorecard } from "./pages/scorecard/Scorecard";
import { Login } from "./pages/login/Login";
import styled from "styled-components";
import { SideBar } from "./components/sidebar/SideBar";
import "react-datepicker/dist/react-datepicker.css";
import { Plan } from "./pages/plan/Plan";
import StrategyMap from "./pages/strategymap/StrategyMap";
import { Timeboard } from "./pages/timeboard/Timeboard";
import { ResetPassword } from "./pages/login/ResetPassword";
import { AuthProvider } from "./context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import Approvals from "./pages/approvals/Approvals";
import HelpAndSupport from "./pages/help/HelpAndSupport";
import ErrorPage from "./pages/error/ErrorPage";
import Reports from "./pages/reports/Reports";
import { Onboarding } from "./pages/onboarding/Onboarding";
import { EvidenceProvider } from "./context/EvidenceContext";
import useAuth from "./hooks/useAuth";
import { TrackingProvider } from "./context/TrackingContext";

import "react-toastify/dist/ReactToastify.css";

interface IProps {
  component: JSX.Element;
}

const SESSION_TIMEOUT = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll"];

const PrivateRoutes = ({ component, ...rest }: IProps | RouteProps) => {
  const auth = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        auth.isAuthenticated() ? (
          component
        ) : (
          <Redirect to={{ pathname: "/login", state: { from: location } }} />
        )
      }
    />
  );
};

const MainPages = () => {
  let { path } = useRouteMatch();

  return (
    <MainWrapper>
      <SideBar />
      <ContentWrapper>
        <Header />
        <Switch>
          <Route path={`${path}/settings`} component={Settings} />
          <Route path="/dashboard" component={Dashboard} />
          <Route path={`${path}/scorecard`} component={Scorecard} />
          <Route path={`${path}/dashboard`} component={Dashboard} />
          <Route path={`${path}/timeboard`} component={Timeboard} />
          <Route path={`${path}/reports`} component={Reports} />
          <Route path={`${path}/plan`} component={Plan} />
          <Route path={`${path}/strategymap`} component={StrategyMap} />
          <Route path={`${path}/approvals`} component={Approvals} />
          <Route path={`${path}/help`} component={HelpAndSupport} />
          <Route path={`${path}/404`} component={ErrorPage} />
        </Switch>
      </ContentWrapper>
    </MainWrapper>
  );
};

const MainWrapper = styled.div`
  display: grid;
  grid-template-columns: 6em auto;
  height: 100vh;
  @media (max-width: 640px) {
    grid-template-columns: 3em auto;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-rows: 4.5em auto;
  @media (max-width: 640px) {
    grid-template-rows: 2.5em auto;
  }
`;

const App = () => {
  const auth = useAuth();

  useEffect(() => {
    const handleUserActivity = () => {
      const currentTime = Date.now().toString();
      localStorage.setItem("lastActivityTime", currentTime);
    };
    
    const checkSessionExpiration = () => {
      const lastActivityTime = parseInt(localStorage.getItem("lastActivityTime") || "0", 10);
      const currentTime = Date.now();
      if (currentTime - lastActivityTime >= SESSION_TIMEOUT) {
        toast.error("Session expired. You will be logged out.");
        setTimeout(handleLogout, 1500);
      }
    };

    const handleLogout = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      localStorage.removeItem("refresh_token");
      window.location.replace("/login");
    };

    // Set initial last activity time
    localStorage.setItem("lastActivityTime", Date.now().toString());

    // Attach activity event listeners
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, handleUserActivity)
    );

    // Monitor session timeout periodically
    const intervalId = setInterval(checkSessionExpiration, 1000);

    // Cleanup event listeners and interval on unmount
    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, handleUserActivity)
      );
    };
  }, [auth]);

  return (
    <BrowserRouter>
      <AuthProvider>
        <EvidenceProvider>
          <TrackingProvider>
          <StyledToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Switch>
            <Route exact path="/">
              <Redirect to="/login" />
            </Route>
            <Route path="/login" component={Login} />
            <Route path="/resetPassword" component={ResetPassword} />
            <Route path="/password-reset" component={ResetPassword} />
            <Route path="/onboarding/:token" component={Onboarding} />
            <PrivateRoutes path="/:id" component={<MainPages />} />
          </Switch>
          </TrackingProvider>
        </EvidenceProvider> {/* Close EvidenceProvider */}
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;

const StyledToastContainer = styled(ToastContainer).attrs({
  className: "toast-container",
  toastClassName: "toast",
  bodyClassName: "body",
  progressClassName: "progress",
})`
  width: 90%;
  height: 0.1em;
  .toast {
    min-height: 3em;
    border-radius: 0.5em;
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    padding: 0;
  }
  .Toastify__toast--error {
    background: #fff7f7;
    color: #ff6666;
  }

  .Toastify__toast--error::before {
    content: "";
    border-left: 1em solid #ff6666;
    border-top: 1em solid #ff6666;
    border-bottom: 1em solid #ff6666;
    border-radius: 1em;
    min-height: 100%;
    z-index: 1000;
  }

  .Toastify__toast--success {
    background: #f1fdf9;
    color: #13c997;
  }

  .Toastify__toast--success::before {
    content: "";
    border-left: 1em solid #13c997;
    border-top: 1em solid #13c997;
    border-bottom: 1em solid #13c997;
    border-radius: 1em;
    min-height: 100%;
    z-index: 1000;
  }
`;
